import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import { Button, Form, Input, DatePicker, Select, Radio, Checkbox } from "antd";
import { useTranslation } from "../../../../utils/onboardingI18n";
import Loader from "../../../../components/Loader.jsx";
import { GetAllCountryApi } from "../../../../api/request/common.js";
import {
  addUpdateCompanyInfo,
  getCompanyInfo,
  getNatureOfBusiness,
  getTaxDomicilesReasons,
} from "../../../../api/request/corporate.js";
import { handleApiError } from "../../../../utils/handleApiError.js";
import { mobileNumberValidator } from "../../../../utils/utils.js";
import AlternateTaxIdentifierField from "../../../../components/onboarding/AlternateTaxIdentifierField.jsx";
import OnboardingMultiSelect from "../../../../components/onboarding/OnboardingMultiSelect.jsx";
import {
  CRS_CLASSIFICATION_OPTIONS,
  CRS_PASSIVE_NFE_WITH_CONTROLLING,
} from "../../../../utils/onboardingEnums.js";

const CompanyInfo = ({ onNext, onPrevious, id, isSubmitted }) => {
  const { t } = useTranslation("onBoarding");
  const [form] = Form.useForm();
  const [countries, setCountries] = useState([]);
  const [countryCodes, setCountryCodes] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [natures, setNatures] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sameAsAbove, setSameAsAbove] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  const isMultiTaxDomicile = Form.useWatch("isMultiTaxDomicileEnabled", form);
  const crsClassifications = Form.useWatch("crsClassifications", form) || [];

  const isDisabled = isSubmitted;

  const getAllCountries = async () => {
    try {
      const { data } = await GetAllCountryApi();
      if (data?.success) {
        setCountries(
          data?.data?.map((item, i) => ({
            key: i,
            value: item?.id,
            label: item?.countryName,
          }))
        );

        setCountryCodes(
          data?.data
            ?.map((item, i) => ({
              key: i,
              value: (item?.dialCode || item?.countryCode1),
              label: `${(item?.dialCode || item?.countryCode1)} (${item?.countryCode2})`,
              countryCode: (item?.dialCode || item?.countryCode1),
            }))
            ?.filter((item) => item?.value)
        );
      }
    } catch (error) {}
  };

  const getAllReasons = async () => {
    try {
      const { data } = await getTaxDomicilesReasons();
      if (data?.success) {
        setReasons(
          data?.data?.map((item) => ({ value: item?.id, label: item?.name }))
        );
      }
    } catch (error) {}
  };

  const getAllNatures = async () => {
    try {
      const { data } = await getNatureOfBusiness();
      if (data?.success) {
        setNatures(
          data?.data?.map((item) => ({ value: item?.id, label: item?.name }))
        );
      }
    } catch (error) {}
  };

  const getCompanyInfoData = async () => {
    try {
      const { data } = await getCompanyInfo(id);
      if (data?.success) {
        const phone = data?.data?.companyPhoneNumber ?? "";
        const [countryCode, phoneNumber] = phone.trim().split(" ");
        const crsClassifications = data?.data?.crsClassifications || [];
        const needsControllingPersons = crsClassifications.includes(
          CRS_PASSIVE_NFE_WITH_CONTROLLING
        );

        form.setFieldsValue({
          ...data?.data,
          dateOfIncorporation: dayjs(data?.data?.dateOfIncorporation) || null,
          countryCode: countryCode || null,
          phoneNumber: phoneNumber || null,
          crsClassifications,
          crsControllingPersons:
            data?.data?.crsControllingPersons?.length > 0
              ? data.data.crsControllingPersons
              : needsControllingPersons
                ? [
                    {
                      fullName: "",
                      taxIdentificationNumber: "",
                      countryOfTaxResidenceId: null,
                      tinNotApplicableReason: "",
                    },
                  ]
                : [],
        });
      }
    } catch (error) {}
  };

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      try {
        const promises = [getAllCountries(), getAllReasons(), getAllNatures()];

        if (id) promises.push(getCompanyInfoData());

        await Promise.all(promises);
      } catch (error) {
        handleApiError(error);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [id]);

  const filterOption = (input, option) => {
    return (option?.label ?? "").toLowerCase().includes(input.toLowerCase());
  };

  const handleMultiTaxResidentChange = (e) => {
    const isMultiTaxDomicile = e?.target?.value;
    const taxDomiciles = form.getFieldValue("taxDomiciles") || [];

    if (isMultiTaxDomicile) {
      if (!taxDomiciles?.length) {
        form.setFieldsValue({
          taxDomiciles: [
            {
              taxDomicileCountryId: null,
              taxIDNumber: "",
              isTINIDAvailable: false,
              taxDomicileReasonID: null,
            },
          ],
        });
      }
    } else {
      if (taxDomiciles?.length) {
        form.setFieldsValue({
          taxDomiciles: [taxDomiciles[0]],
        });
      }
    }
  };

  const handleSameAsAboveChange = (e) => {
    const checked = e.target.checked;
    setSameAsAbove(checked);

    if (checked) {
      const registeredAddress = form.getFieldsValue([
        "companyApartmentVillaInfo",
        "companyStreetAreaInfo",
        "companyCountryCode",
        "companyCity",
        "companyZipCode",
      ]);

      form.setFieldsValue({
        businessApartmentVillaInfo: registeredAddress.companyApartmentVillaInfo,
        businessStreetAreaInfo: registeredAddress.companyStreetAreaInfo,
        businessCountryCode: registeredAddress.companyCountryCode,
        businessCity: registeredAddress.companyCity,
        businessZipCode: registeredAddress.companyZipCode,
      });
    } else {
      form.setFieldsValue({
        businessApartmentVillaInfo: "",
        businessStreetAreaInfo: "",
        businessCountryCode: undefined,
        businessCity: "",
        businessZipCode: "",
      });
    }
  };

  const handleRegisteredAddressChange = () => {
    if (sameAsAbove) {
      const registeredAddress = form.getFieldsValue([
        "companyApartmentVillaInfo",
        "companyStreetAreaInfo",
        "companyCountryCode",
        "companyCity",
        "companyZipCode",
      ]);

      form.setFieldsValue({
        businessApartmentVillaInfo: registeredAddress.companyApartmentVillaInfo,
        businessStreetAreaInfo: registeredAddress.companyStreetAreaInfo,
        businessCountryCode: registeredAddress.companyCountryCode,
        businessCity: registeredAddress.companyCity,
        businessZipCode: registeredAddress.companyZipCode,
      });
    }
  };

  const handleSubmit = async (values) => {
    if (isDisabled) return;
    setIsSubmitLoading(true);
    try {
      const payload = {
        ...values,
        companyPhoneNumber: `${values?.countryCode} ${values?.phoneNumber}`,
        taxDomiciles:
          values?.taxDomiciles?.map((item) => {
            const value = {
              ...item,
              isTINIDAvailable: item?.isTINIDAvailable || false,
              alternateTaxIdentifier: item?.alternateTaxIdentifier || "",
            };
            if (value.isTINIDAvailable) {
              value.taxIDNumber = "";
            } else {
              value.taxDomicileReasonID = null;
              value.alternateTaxIdentifier = "";
            }
            return value;
          }) || [],
        crsClassifications: values?.crsClassifications || [],
        crsControllingPersons: (values?.crsClassifications || []).includes(
          CRS_PASSIVE_NFE_WITH_CONTROLLING
        )
          ? values?.crsControllingPersons || []
          : [],
      };

      if (id) Object.assign(payload, { id });

      const { data } = await addUpdateCompanyInfo(payload);

      if (data?.success) {
        toast.success(data?.message);
        onNext();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitLoading(false);
    }
  };

  if (isLoading) return <Loader />;

  return (
    <div className="max-w-5xl mx-auto p-6">
      <Form
        form={form}
        size="large"
        layout="vertical"
        autoComplete="off"
        disabled={isDisabled}
        onFinish={handleSubmit}
        // validateTrigger={['onChange', 'onBlur', 'onSubmit']}
        initialValues={{
          isMultiTaxDomicileEnabled: false,
          crsClassifications: [],
          crsControllingPersons: [
            {
              fullName: "",
              taxIdentificationNumber: "",
              countryOfTaxResidenceId: null,
              tinNotApplicableReason: "",
            },
          ],
          taxDomiciles: [
            {
              taxDomicileCountryId: null,
              taxIDNumber: "",
              isTINIDAvailable: false,
              taxDomicileReasonID: null,
              alternateTaxIdentifier: "",
            },
          ],
        }}
      >
        <div className="border border-gray-300 rounded-lg p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Company Name */}
            <Form.Item
              label={t("Company Name")}
              name="companyName"
              rules={[{ required: true, message: t("Please enter company name") }]}
            >
              <Input placeholder={t("Enter company name")} />
            </Form.Item>

            {/* Date Of Incorporation */}
            <Form.Item
              label={t("Date Of Incorporation")}
              name="dateOfIncorporation"
              rules={[
                {
                  required: true,
                  message: t("Please select date of incorporation"),
                },
              ]}
            >
              <DatePicker
                className="w-full"
                placeholder={t("Select date")}
                format="DD-MM-YYYY"
              />
            </Form.Item>

            {/* Country of Incorporation */}
            <Form.Item
              label={t("Country of Incorporation")}
              name="placeOfIncCorporation"
              rules={[
                {
                  required: true,
                  message: t("Please select country of incorporation"),
                },
              ]}
            >
              <Select
                showSearch
                placeholder={t("Select country")}
                filterOption={filterOption}
                options={countries}
              />
            </Form.Item>

            {/* Company Registration Number */}
            <Form.Item
              label={t("Company Registration Number")}
              name="companyRegistrationNumber"
              rules={[
                {
                  required: true,
                  message: t("Please enter company registration number"),
                },
              ]}
            >
              <Input placeholder={t("Enter company registration number")} />
            </Form.Item>

            {/* Nature of Business */}
            <Form.Item
              label={t("Nature of Business")}
              name="natureOfBusinessId"
              rules={[
                { required: true, message: t("Please select nature of business") },
              ]}
            >
              <Select
                showSearch
                placeholder={t("Select nature of business")}
                options={natures}
                filterOption={filterOption}
              />
            </Form.Item>

            {/* Principal Business Activity */}
            <Form.Item
              label={t("Principal Business Activity")}
              name="businessActivity"
              rules={[
                {
                  required: true,
                  message: t("Please enter principal business activity"),
                },
              ]}
            >
              <Input placeholder={t("Enter principal business activity")} />
            </Form.Item>

            {/* Company's Website */}
            <Form.Item
              label={t("Company's Website")}
              name="companyWebsite"
              rules={[
                { required: true, message: t("Please enter company website") },
                { type: "url", message: t("Please enter a valid URL") },
              ]}
            >
              <Input placeholder="https://example.com" />
            </Form.Item>

            {/* Email in Incorporation */}
            <Form.Item
              label={t("Email in Incorporation")}
              name="companyEmail"
              rules={[
                { required: true, message: t("Please enter email") },
                { type: "email", message: t("Please enter a valid email") },
              ]}
            >
              <Input type="email" placeholder={t("Enter email")} />
            </Form.Item>

            {/* Mobile Number with Country Code */}
            <Form.Item label={t("Mobile Number")} required>
              <div className="flex w-full gap-2">
                <Form.Item
                  name="countryCode"
                  noStyle
                  rules={[{ required: true, message: t("Country code required") }]}
                >
                  <Select
                    showSearch
                    placeholder={t("Code")}
                    filterOption={filterOption}
                    options={countryCodes}
                    style={{ width: "30%" }}
                  />
                </Form.Item>
                <Form.Item
                  name="phoneNumber"
                  noStyle
                  rules={[mobileNumberValidator()]}
                >
                  <Input
                    placeholder={t("Enter mobile number")}
                    maxLength={15}
                    inputMode="numeric"
                    onKeyPress={(e) => {
                      if (!/[0-9]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                  />
                </Form.Item>
              </div>
            </Form.Item>

            {/* Does the entity have a place of business in the United States? */}
            <Form.Item
              label={t("Does the entity have a place of business in the United States?")}
              name="isBusinessInUS"
              rules={[{ required: true, message: t("Please select an option") }]}
            >
              <Radio.Group>
                <Radio value={true}>{t("Yes")}</Radio>
                <Radio value={false}>{t("No")}</Radio>
              </Radio.Group>
            </Form.Item>

            {/* Tax Domicile */}
            <Form.Item
              label={t("Tax Domicile")}
              name="taxDomicile"
              rules={[
                { required: true, message: t("Please select tax domicile") },
              ]}
            >
              <Select
                showSearch
                placeholder={t("Select tax domicile")}
                filterOption={filterOption}
                options={countries}
              />
            </Form.Item>

            <Form.Item
              label={t("Legal Entity Identifier (LEI)")}
              name="legalEntityidentify"
            >
              <Input placeholder={t("Enter LEI (optional)")} />
            </Form.Item>

            <Form.Item
              label={t("CRS Classification")}
              name="crsClassifications"
              rules={[
                {
                  required: true,
                  message: t("Please select at least one CRS classification"),
                },
              ]}
              className="md:col-span-2"
            >
              <OnboardingMultiSelect
                placeholder={t("Select CRS classification")}
                options={CRS_CLASSIFICATION_OPTIONS}
              />
            </Form.Item>
          </div>
        </div>

        {crsClassifications.includes(CRS_PASSIVE_NFE_WITH_CONTROLLING) && (
          <div className="border border-gray-300 rounded-lg p-6 mt-6">
            <h3 className="text-lg font-semibold mb-4">
              {t("CRS Controlling Persons")}
            </h3>
            <Form.List name="crsControllingPersons">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field, index) => (
                    <div
                      key={field.key}
                      className="border border-gray-200 rounded-lg p-4 mb-4"
                    >
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="font-medium">
                          {t("Controlling Person")} {index + 1}
                        </h4>
                        {fields.length > 1 && (
                          <Button type="link" danger onClick={() => remove(field.name)}>
                            {t("Remove")}
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Form.Item
                          label={t("Full Name")}
                          name={[field.name, "fullName"]}
                          rules={[
                            { required: true, message: t("Please enter full name") },
                          ]}
                        >
                          <Input placeholder={t("Enter full name")} />
                        </Form.Item>
                        <Form.Item
                          label={t("Tax Identification Number")}
                          name={[field.name, "taxIdentificationNumber"]}
                        >
                          <Input placeholder={t("Enter TIN")} />
                        </Form.Item>
                        <Form.Item
                          label={t("Country of Tax Residence")}
                          name={[field.name, "countryOfTaxResidenceId"]}
                          rules={[
                            {
                              required: true,
                              message: t("Please select country of tax residence"),
                            },
                          ]}
                        >
                          <Select
                            showSearch
                            placeholder={t("Select country")}
                            filterOption={filterOption}
                            options={countries}
                          />
                        </Form.Item>
                        <Form.Item shouldUpdate noStyle>
                          {({ getFieldValue }) => {
                            const tin = getFieldValue([
                              "crsControllingPersons",
                              index,
                              "taxIdentificationNumber",
                            ]);
                            if (tin) return null;
                            return (
                              <Form.Item
                                label={t("TIN Not Applicable Reason")}
                                name={[field.name, "tinNotApplicableReason"]}
                                rules={[
                                  {
                                    required: true,
                                    message: t("Please enter reason"),
                                  },
                                ]}
                              >
                                <Input placeholder={t("Enter reason")} />
                              </Form.Item>
                            );
                          }}
                        </Form.Item>
                      </div>
                    </div>
                  ))}
                  <Button
                    type="dashed"
                    block
                    onClick={() =>
                      add({
                        fullName: "",
                        taxIdentificationNumber: "",
                        countryOfTaxResidenceId: null,
                        tinNotApplicableReason: "",
                      })
                    }
                  >
                    {t("+ Add Controlling Person")}
                  </Button>
                </>
              )}
            </Form.List>
          </div>
        )}

        {/* Tax Resident Section */}
        <div className="mt-6">
          <p className="text-sm text-gray-600 !font-normal mb-4">
            {t("For Common Reporting Standard (CRS) purposes, please mention which countries you are tax resident of? *")}
          </p>
          <div className="border border-gray-300 rounded-lg p-6 mt-6">
            <div className="mb-6">
              <Form.Item
                label={t("Are there any other countries you are tax resident of?")}
                name="isMultiTaxDomicileEnabled"
                rules={[{ required: true, message: t("Please select an option") }]}
              >
                <Radio.Group onChange={handleMultiTaxResidentChange}>
                  <Radio value={true}>{t("Yes")}</Radio>
                  <Radio value={false}>{t("No")}</Radio>
                </Radio.Group>
              </Form.Item>
            </div>

            <Form.List name="taxDomiciles">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field, index) => (
                    <div
                      key={field.key}
                      className="border border-gray-300 rounded-lg p-6 mt-6"
                    >
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold">
                          {index === 0
                            ? t("Tax Resident Information")
                            : `${t("Additional Tax Resident Information")} ${index}`}
                        </h3>

                        {index !== 0 && (
                          <Button
                            type="link"
                            danger
                            onClick={() => remove(field.name)}
                          >
                            {t("Remove")}
                          </Button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Form.Item
                          label={t("Select country")}
                          name={[field.name, "taxDomicileCountryId"]}
                          rules={[
                            {
                              required: true,
                              message: t("Please select country"),
                            },
                          ]}
                        >
                          <Select
                            showSearch
                            placeholder={t("Select country")}
                            filterOption={filterOption}
                            options={countries}
                          />
                        </Form.Item>

                        <Form.Item shouldUpdate noStyle>
                          {({ getFieldValue }) => {
                            const noTIN = getFieldValue([
                              "taxDomiciles",
                              index,
                              "isTINIDAvailable",
                            ]);

                            return (
                              <Form.Item
                                label={t("Tax Identification Number")}
                                name={[field.name, "taxIDNumber"]}
                                rules={
                                  noTIN
                                    ? []
                                    : [{ required: true, message: t("Enter TIN") }]
                                }
                                className="w-full"
                                disabled={noTIN || isDisabled}
                              >
                                <Input
                                  placeholder={t("Enter tax identification number")}
                                  disabled={noTIN || isDisabled}
                                />
                              </Form.Item>
                            );
                          }}
                        </Form.Item>

                        <Form.Item
                          name={[field.name, "isTINIDAvailable"]}
                          valuePropName="checked"
                          className="md:col-span-2"
                        >
                          <Checkbox>{t("Check if you don't have TIN?")}</Checkbox>
                        </Form.Item>

                        <Form.Item shouldUpdate noStyle>
                          {({ getFieldValue }) => {
                            const isTIN = !getFieldValue([
                              "taxDomiciles",
                              index,
                              "isTINIDAvailable",
                            ]);

                            if (isTIN) return null;

                            return (
                              <Form.Item
                                label={t("Please select a reason")}
                                name={[field.name, "taxDomicileReasonID"]}
                                rules={[
                                  {
                                    required: true,
                                    message: t("Please select reason"),
                                  },
                                ]}
                                className="md:col-span-2"
                              >
                                <Radio.Group>
                                  {reasons.map((r) => (
                                    <Radio
                                      key={r.value}
                                      value={r.value}
                                      className="block mb-2"
                                    >
                                      {r.label}
                                    </Radio>
                                  ))}
                                </Radio.Group>
                              </Form.Item>
                            );
                          }}
                        </Form.Item>

                        <AlternateTaxIdentifierField
                          field={field}
                          index={index}
                          isDisabled={isDisabled}
                          label={t("Business / License Number")}
                          t={t}
                        />
                      </div>
                    </div>
                  ))}

                  {isMultiTaxDomicile && (
                    <div className="mt-6">
                      <Button
                        type="dashed"
                        block
                        onClick={() =>
                          add({
                            taxDomicileCountryId: null,
                            taxIDNumber: "",
                            isTINIDAvailable: false,
                            taxDomicileReasonID: null,
                            alternateTaxIdentifier: "",
                          })
                        }
                      >
                        {t("+ Add Another Tax Resident Country")}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </Form.List>
          </div>
        </div>

        {/* Registered Address Section */}
        <div className="border border-gray-300 rounded-lg p-6 mt-6">
          <h3 className="text-lg font-semibold mb-4">{t("Registered address")}</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Apartment/Villa Number/Name */}
            <Form.Item
              label={t("Apartment/Villa Number/Name")}
              name="companyApartmentVillaInfo"
              rules={[
                {
                  required: true,
                  message: t("Please enter apartment/villa number/name"),
                },
              ]}
            >
              <Input
                placeholder={t("Enter apartment/villa number/name")}
                onChange={handleRegisteredAddressChange}
              />
            </Form.Item>

            {/* Street Name/Area */}
            <Form.Item
              label={t("Street Name/Area")}
              name="companyStreetAreaInfo"
              rules={[
                { required: true, message: t("Please enter street name/area") },
              ]}
            >
              <Input
                placeholder={t("Enter street name/area")}
                onChange={handleRegisteredAddressChange}
              />
            </Form.Item>

            {/* Country */}
            <Form.Item
              label={t("Country")}
              name="companyCountryCode"
              rules={[{ required: true, message: t("Please select country") }]}
            >
              <Select
                showSearch
                placeholder={t("Select country")}
                filterOption={filterOption}
                options={countries}
                onChange={handleRegisteredAddressChange}
              />
            </Form.Item>

            {/* City / Town */}
            <Form.Item
              label={t("City / Town")}
              name="companyCity"
              rules={[{ required: true, message: t("Please enter city/town") }]}
            >
              <Input
                placeholder={t("Enter city/town")}
                onChange={handleRegisteredAddressChange}
              />
            </Form.Item>

            {/* Postal / Zip Code */}
            <Form.Item
              label={t("Postal / Zip Code")}
              name="companyZipCode"
              rules={[
                { required: true, message: t("Please enter postal/zip code") },
              ]}
            >
              <Input
                placeholder={t("Enter postal/zip code")}
                onChange={handleRegisteredAddressChange}
              />
            </Form.Item>
          </div>
        </div>

        {/* Business Address Section */}
        <div className="border border-gray-300 rounded-lg p-6 mt-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">{t("Business address")}</h3>
            <Form.Item
              name="IsSameAsAbove"
              valuePropName="checked"
              className="mb-0"
            >
              <Checkbox onChange={handleSameAsAboveChange}>
                {t("Same as Above")}
              </Checkbox>
            </Form.Item>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Apartment/Villa Number/Name */}
            <Form.Item
              label={t("Apartment/Villa Number/Name")}
              name="businessApartmentVillaInfo"
              rules={[
                {
                  required: true,
                  message: t("Please enter apartment/villa number/name"),
                },
              ]}
            >
              <Input
                placeholder={t("Enter apartment/villa number/name")}
                disabled={isDisabled || sameAsAbove}
              />
            </Form.Item>

            {/* Street Name/Area */}
            <Form.Item
              label={t("Street Name/Area")}
              name="businessStreetAreaInfo"
              rules={[
                { required: true, message: t("Please enter street name/area") },
              ]}
            >
              <Input
                placeholder={t("Enter street name/area")}
                disabled={isDisabled || sameAsAbove}
              />
            </Form.Item>

            {/* Country */}
            <Form.Item
              label={t("Country")}
              name="businessCountryCode"
              rules={[{ required: true, message: t("Please select country") }]}
            >
              <Select
                showSearch
                placeholder={t("Select country")}
                filterOption={filterOption}
                options={countries}
                disabled={isDisabled || sameAsAbove}
              />
            </Form.Item>

            {/* City / Town */}
            <Form.Item
              label={t("City / Town")}
              name="businessCity"
              rules={[{ required: true, message: t("Please enter city/town") }]}
            >
              <Input
                placeholder={t("Enter city/town")}
                disabled={isDisabled || sameAsAbove}
              />
            </Form.Item>

            {/* Postal / Zip Code */}
            <Form.Item
              label={t("Postal / Zip Code")}
              name="businessZipCode"
              rules={[
                { required: true, message: t("Please enter postal/zip code") },
              ]}
            >
              <Input
                placeholder={t("Enter postal/zip code")}
                disabled={isDisabled || sameAsAbove}
              />
            </Form.Item>
          </div>
        </div>

        <div className="flex justify-between mt-8">
          <Button onClick={onPrevious} disabled={true}>
            {t("Previous")}
          </Button>

          {isDisabled ? (
            <Button type="primary" onClick={onNext} disabled={false}>
              {t("Next")}
            </Button>
          ) : (
            <Button type="primary" htmlType="submit" loading={isSubmitLoading}>
              {isSubmitLoading ? t("Saving...") : t("Next")}
            </Button>
          )}
        </div>
      </Form>
    </div>
  );
};

export default CompanyInfo;
